import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import { loadScenarioSchema } from "../services/loadDemandContract";
import {
  pvBatteryConfigurationSchema,
  pvInverterRedundancyModeSchema,
  pvPhaseConfigurationSchema,
  pvSystemTypeSchema,
  type PVBatteryConfiguration,
  type PVInverterRedundancyMode,
  type PVPhaseConfiguration,
  type PVSystemType,
} from "../services/pvSizingContract";
import { deriveStandbyUnits } from "../utils/sourceSizing";
import type { ValidationLabels } from "../utils/validationMessages";

// Draft model of the Solar PV sizing study form (EOS-03d): what the user is
// typing, kept apart from the components so the payload rules are tested
// without a browser.
//
// Every value is held as the string the user typed, including the counts. A
// blank optional field is left OUT of the payload: under Master Prompt A16 (a)
// and A19 (h) a blank factor or AC efficiency means NOT ESTABLISHED, and the
// engine must see it as absent, never as "" and never as "1". The module data,
// the inverter window, both temperatures and the target DC/AC ratio are
// design-basis inputs (A19 (g)): required, so a blank one is sent as "" for the
// schema to report.
//
// The redundancy mode has no default in the backend, so the draft starts with
// none selected ("") and a blank choice is left out for the schema to report as
// required. System type, phase configuration, battery configuration and
// scenario are descriptors: optional, no default, echoed in the result, so a
// blank one is simply left out. The DG question is a three-way choice: not
// stated (left out), yes or no (A19 (k)).
//
// The available inverter ratings are separate entries with stable ids, so
// adding or removing one never disturbs the others; the ids never reach the
// backend. Every entry is sent in the order it is on screen, blank ones
// included - the backend requires a unique ascending schedule of positive
// decimals and must be the one to say so, entry by entry.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const PV_STUDY_DRAFT_MODULE = "pv-sizing";

/** The three answers to "operates alongside a DG": not stated, yes or no. */
export const PV_DG_COEXISTENCE_CHOICES = ["", "yes", "no"] as const;

export type PVDGCoexistenceChoice = (typeof PV_DG_COEXISTENCE_CHOICES)[number];

export type PVRatingDraft = {
  id: string;
  value: string;
};

export type PVStudyDraft = {
  code: string;
  name: string;
  requiredAcOutputKw: string;
  acEfficiencyFactor: string;
  targetDcAcRatio: string;
  moduleRatedPowerWp: string;
  moduleOpenCircuitVoltageV: string;
  moduleMaximumPowerVoltageV: string;
  moduleShortCircuitCurrentA: string;
  temperatureCoefficientVocPercentPerC: string;
  temperatureCoefficientVmpPercentPerC: string;
  minimumDesignTemperatureC: string;
  maximumCellTemperatureC: string;
  inverterMaxDcVoltageV: string;
  inverterMpptMinVoltageV: string;
  inverterMpptMaxVoltageV: string;
  inverterMaxInputCurrentPerMpptA: string;
  mpptCount: string;
  maximumStringsPerMppt: string;
  futureGrowthFactor: string;
  designMarginFactor: string;
  ambientDeratingFactor: string;
  altitudeDeratingFactor: string;
  dutyInverters: string;
  // Derived from the redundancy mode and the duty inverters; shown, never typed.
  redundantInverters: string;
  redundancyMode: string;
  exportLimitKw: string;
  dgCoexistence: PVDGCoexistenceChoice;
  systemType: string;
  phaseConfiguration: string;
  batteryConfiguration: string;
  scenario: string;
  jurisdictionProfile: string;
  notes: string;
  /** The source picked from the revision's diagram, or "" (A28 (d)). */
  sldElementCode: string;
  unitRatings: PVRatingDraft[];
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values the contract knows or "".
// A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const pvStudyDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    requiredAcOutputKw: z.string(),
    acEfficiencyFactor: z.string(),
    targetDcAcRatio: z.string(),
    moduleRatedPowerWp: z.string(),
    moduleOpenCircuitVoltageV: z.string(),
    moduleMaximumPowerVoltageV: z.string(),
    moduleShortCircuitCurrentA: z.string(),
    temperatureCoefficientVocPercentPerC: z.string(),
    temperatureCoefficientVmpPercentPerC: z.string(),
    minimumDesignTemperatureC: z.string(),
    maximumCellTemperatureC: z.string(),
    inverterMaxDcVoltageV: z.string(),
    inverterMpptMinVoltageV: z.string(),
    inverterMpptMaxVoltageV: z.string(),
    inverterMaxInputCurrentPerMpptA: z.string(),
    mpptCount: z.string(),
    maximumStringsPerMppt: z.string(),
    futureGrowthFactor: z.string(),
    designMarginFactor: z.string(),
    ambientDeratingFactor: z.string(),
    altitudeDeratingFactor: z.string(),
    dutyInverters: z.string(),
    redundantInverters: z.string(),
    redundancyMode: chosenOrBlank(pvInverterRedundancyModeSchema),
    exportLimitKw: z.string(),
    dgCoexistence: z.enum(PV_DG_COEXISTENCE_CHOICES),
    systemType: chosenOrBlank(pvSystemTypeSchema),
    phaseConfiguration: chosenOrBlank(pvPhaseConfigurationSchema),
    batteryConfiguration: chosenOrBlank(pvBatteryConfigurationSchema),
    scenario: chosenOrBlank(loadScenarioSchema),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
    // A draft kept before the field existed comes back as it was, with no source chosen.
    sldElementCode: z.string().default(""),
    unitRatings: z.array(z.object({ id: z.string(), value: z.string() }).strict()).min(1),
  })
  .strict();

let nextRatingNumber = 1;

// A prefix of its own, so a PV rating id is never mistaken for another form's.
export function createPVRatingId(): string {
  const id = `pv-rating-${nextRatingNumber}`;
  nextRatingNumber += 1;
  return id;
}

// Rating ids come from a counter that starts again at 1 on every page load, while a
// restored draft brings the ids it was saved with. Moving the counter past every id in use
// keeps an entry added afterwards from taking the id of one already on screen.
export function seedPVRatingIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^pv-rating-(\d+)$/.exec(id);
    if (match !== null) {
      nextRatingNumber = Math.max(nextRatingNumber, Number(match[1]) + 1);
    }
  }
}

export function createPVRatingDraft(): PVRatingDraft {
  return { id: createPVRatingId(), value: "" };
}

// Only the defaults the backend itself has are filled in: one duty inverter, no
// redundant inverter and the IN profile. Every design-basis input, the redundancy
// mode, the DG question and every descriptor start blank.
export function createInitialPVStudyDraft(): PVStudyDraft {
  return {
    code: "",
    name: "",
    requiredAcOutputKw: "",
    acEfficiencyFactor: "",
    targetDcAcRatio: "",
    moduleRatedPowerWp: "",
    moduleOpenCircuitVoltageV: "",
    moduleMaximumPowerVoltageV: "",
    moduleShortCircuitCurrentA: "",
    temperatureCoefficientVocPercentPerC: "",
    temperatureCoefficientVmpPercentPerC: "",
    minimumDesignTemperatureC: "",
    maximumCellTemperatureC: "",
    inverterMaxDcVoltageV: "",
    inverterMpptMinVoltageV: "",
    inverterMpptMaxVoltageV: "",
    inverterMaxInputCurrentPerMpptA: "",
    mpptCount: "",
    maximumStringsPerMppt: "",
    futureGrowthFactor: "",
    designMarginFactor: "",
    ambientDeratingFactor: "",
    altitudeDeratingFactor: "",
    dutyInverters: "1",
    redundantInverters: "0",
    redundancyMode: "",
    exportLimitKw: "",
    dgCoexistence: "",
    systemType: "",
    phaseConfiguration: "",
    batteryConfiguration: "",
    scenario: "",
    jurisdictionProfile: "IN",
    notes: "",
    sldElementCode: "",
    unitRatings: [createPVRatingDraft()],
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// A count is a whole number on the contract. A clean whole number is sent as
// one; anything else is passed through as typed, so the schema reports it
// instead of this function guessing what the engineer meant.
function countValue(typed: string): unknown {
  const value = typed.trim();
  return /^\d+$/.test(value) ? Number(value) : typed;
}

// The DG question: not stated is left out; yes and no are sent as booleans (A19 (k)).
function dgCoexistence(choice: PVDGCoexistenceChoice): Record<string, boolean> {
  return choice === "" ? {} : { dg_coexistence: choice === "yes" };
}

// The request body as the form will send it. It is validated afterwards with
// pvRunCreateRequestSchema, so this function does not judge the values and
// never reorders the rating schedule. The redundant count follows the shared
// EOS-03 redundancy rule (the backend schemas agree).
export function buildPVRunPayload(
  draft: PVStudyDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      required_ac_output_kw: draft.requiredAcOutputKw.trim(),
      module_rated_power_wp: draft.moduleRatedPowerWp.trim(),
      module_open_circuit_voltage_v: draft.moduleOpenCircuitVoltageV.trim(),
      module_maximum_power_voltage_v: draft.moduleMaximumPowerVoltageV.trim(),
      module_short_circuit_current_a: draft.moduleShortCircuitCurrentA.trim(),
      temperature_coefficient_voc_percent_per_c: draft.temperatureCoefficientVocPercentPerC.trim(),
      temperature_coefficient_vmp_percent_per_c: draft.temperatureCoefficientVmpPercentPerC.trim(),
      minimum_design_temperature_c: draft.minimumDesignTemperatureC.trim(),
      maximum_cell_temperature_c: draft.maximumCellTemperatureC.trim(),
      inverter_max_dc_voltage_v: draft.inverterMaxDcVoltageV.trim(),
      inverter_mppt_min_voltage_v: draft.inverterMpptMinVoltageV.trim(),
      inverter_mppt_max_voltage_v: draft.inverterMpptMaxVoltageV.trim(),
      inverter_max_input_current_per_mppt_a: draft.inverterMaxInputCurrentPerMpptA.trim(),
      mppt_count: countValue(draft.mpptCount),
      maximum_strings_per_mppt: countValue(draft.maximumStringsPerMppt),
      target_dc_ac_ratio: draft.targetDcAcRatio.trim(),
      // Every entry is sent, blank ones as "", so the index in a message such as
      // "Inverter rating 2 is required." always matches the entry's position on
      // screen. The schema refuses the blank; this builder does not hide it.
      available_inverter_ratings_kw: draft.unitRatings.map((rating) => rating.value.trim()),
      // A16 (a), A19 (h): blank = not established (left out), never "1".
      ...optional("ac_efficiency_factor", draft.acEfficiencyFactor),
      ...optional("future_growth_factor", draft.futureGrowthFactor),
      ...optional("design_margin_factor", draft.designMarginFactor),
      ...optional("ambient_derating_factor", draft.ambientDeratingFactor),
      ...optional("altitude_derating_factor", draft.altitudeDeratingFactor),
      duty_inverters: countValue(draft.dutyInverters),
      redundant_inverters: countValue(
        deriveStandbyUnits(draft.redundancyMode, draft.dutyInverters),
      ),
      ...optional("redundancy_mode", draft.redundancyMode),
      // Descriptors (A19 (j)): left out when not declared.
      ...optional("system_type", draft.systemType),
      ...optional("phase_configuration", draft.phaseConfiguration),
      ...optional("battery_configuration", draft.batteryConfiguration),
      ...optional("scenario", draft.scenario),
      // Echoed only (A19 (k)): an export limit when given, the DG answer when stated.
      ...optional("export_limit_kw", draft.exportLimitKw),
      ...dgCoexistence(draft.dgCoexistence),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
      ...optional("sld_element_code", draft.sldElementCode),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// On-screen labels of the PV study form, keyed by the request path.
// The run request nests the study under "study", so every study path carries
// that prefix; describeLocation numbers the schedule entries, giving
// "Inverter rating 2 - ...". Shared factors carry the Transformer form's wording.
export const PV_STUDY_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.sld_element_code": "Source from the diagram",
    "study.required_ac_output_kw": "Required AC output (kW)",
    "study.ac_efficiency_factor": "AC efficiency (inverter to delivery point)",
    "study.target_dc_ac_ratio": "Target DC/AC ratio",
    "study.module_rated_power_wp": "Module rated power (Wp)",
    "study.module_open_circuit_voltage_v": "Module open-circuit voltage Voc (V)",
    "study.module_maximum_power_voltage_v": "Module maximum-power voltage Vmp (V)",
    "study.module_short_circuit_current_a": "Module short-circuit current Isc (A)",
    "study.temperature_coefficient_voc_percent_per_c": "Voc temperature coefficient (%/°C)",
    "study.temperature_coefficient_vmp_percent_per_c": "Vmp temperature coefficient (%/°C)",
    "study.minimum_design_temperature_c": "Minimum design temperature (°C)",
    "study.maximum_cell_temperature_c": "Maximum cell temperature (°C)",
    "study.inverter_max_dc_voltage_v": "Inverter maximum DC voltage (V)",
    "study.inverter_mppt_min_voltage_v": "MPPT minimum voltage (V)",
    "study.inverter_mppt_max_voltage_v": "MPPT maximum voltage (V)",
    "study.inverter_max_input_current_per_mppt_a": "Maximum input current per MPPT (A)",
    "study.mppt_count": "MPPT inputs per inverter",
    "study.maximum_strings_per_mppt": "Maximum strings per MPPT",
    "study.available_inverter_ratings_kw": "Inverter ratings",
    "study.future_growth_factor": "Future growth factor",
    "study.design_margin_factor": "Design margin factor",
    "study.ambient_derating_factor": "Ambient derating factor",
    "study.altitude_derating_factor": "Altitude derating factor",
    "study.duty_inverters": "Duty inverters",
    "study.redundant_inverters": "Redundant inverters",
    "study.redundancy_mode": "Redundancy mode",
    "study.export_limit_kw": "Export limit (kW)",
    "study.dg_coexistence": "Operates alongside a DG",
    "study.system_type": "System type",
    "study.phase_configuration": "Phase configuration",
    "study.battery_configuration": "Battery configuration",
    "study.scenario": "Scenario",
    "study.jurisdiction_profile": "Jurisdiction profile",
    notes: "Study notes",
  },
  // One entry of the rating schedule: "Inverter rating 2".
  groups: { available_inverter_ratings_kw: "Inverter rating" },
};

// The words of every choice the form offers, typed against the contract enums
// so a value the contract gains cannot pass without a label. The redundancy
// wording follows the Transformer, DG and UPS forms, with "inverter" in place of
// "unit". The scenario words are the load module's (SCENARIO_LABELS in LoadRow).

export const PV_REDUNDANCY_LABELS: Record<PVInverterRedundancyMode, string> = {
  NONE: "None (no redundant inverter)",
  N_PLUS_1: "N+1 (one redundant inverter)",
  TWO_N: "2N (a redundant inverter for every duty inverter)",
};

export const PV_REDUNDANCY_HINTS: Record<PVInverterRedundancyMode, string> = {
  NONE: "None: no redundant inverter.",
  N_PLUS_1: "N+1: one redundant inverter.",
  TWO_N: "2N: equal to duty inverters.",
};

export const PV_SYSTEM_TYPE_LABELS: Record<PVSystemType, string> = {
  GRID_TIED: "Grid-tied",
  HYBRID: "Hybrid (grid and battery)",
  OFF_GRID: "Off-grid",
};

export const PV_PHASE_CONFIGURATION_LABELS: Record<PVPhaseConfiguration, string> = {
  SINGLE_PHASE: "Single-phase",
  THREE_PHASE: "Three-phase",
};

export const PV_BATTERY_CONFIGURATION_LABELS: Record<PVBatteryConfiguration, string> = {
  NONE: "None",
  DC_COUPLED: "DC-coupled",
  AC_COUPLED: "AC-coupled",
};

export const PV_DG_COEXISTENCE_LABELS: Record<PVDGCoexistenceChoice, string> = {
  "": "Not stated",
  yes: "Yes",
  no: "No",
};
